import React from 'react';
import { Dispatch } from 'redux';
import { connect } from "react-redux";
import { IApplicationState, RootActionCreators } from '../../../store';
import { createStyles, makeStyles, useTheme, Theme } from '@material-ui/core/styles';
import { RouteComponentProps } from 'react-router';
import { IReportDataObject } from '../../../entities/reports/IReportData';
import { IData as IContactpointDetail } from '../../../store/contactpoint/ContactpointItem';
import { INavigations, mapNavigationsToProps, ContactpointNavigation, ContactpointNavigationPage } from '../../organisms/contactpoint/ContactpointNavigation';
import { PageHeader } from '../../organisms/PageHeader';
import { ContactpointDetail } from '../../organisms/contactpoint/ContactpointDetail';
import { FetchState } from '../../../entities/FetchState';
import { RefreshButton } from '../../atoms/buttons/RefreshButton';
import { EditButton } from '../../atoms/buttons/EditButton';
import { DeleteButton } from '../../atoms/buttons/DeleteButton';
import { useTranslation } from 'react-i18next';
import ErrorMessage from '../../organisms/ErrorMessage';
import { DeletingMessage } from '../../organisms/DeletingMessage';

// CSS
const useStyles = makeStyles((theme: Theme) =>
    createStyles({}),
);

interface IMatchParams {
    id: string;
}
interface IPropsFromParams extends RouteComponentProps<IMatchParams> { }

// Standard Properties from parent component
interface IProps { }

// Outgoing functions to parent component
interface IDispatch {}

// Properties from State 
interface IStateProps {
  details: IReportDataObject<IContactpointDetail>;
  deleteState: IReportDataObject<IContactpointDetail>;
}
const mapStateProps = (state: IApplicationState): IStateProps => ({
    details: state.contactpoint.contactpointItem.Item,
    deleteState: state.contactpoint.contactpointItem.DeleteItem
});

// Outgoing functions to Dispatch
interface IDispatchProps {
    navigations: INavigations;
    onLoadData: (id: string) => void;
    onEdit: (id: string) => void;
    onDelete: (id: string) => void;
}
const mapDispatchProps = (dispatch: Dispatch): IDispatchProps => ({
    navigations: mapNavigationsToProps(dispatch),
    onLoadData: (id: string) => dispatch(RootActionCreators.Contactpoint.contactpointItem.FetchData(id)),
    onEdit: (id: string) => dispatch(RootActionCreators.Navigate.ToContactpointEdit(id)),
    onDelete: (id: string) => dispatch(RootActionCreators.Contactpoint.contactpointItem.DeleteData(id)),
});

// Actual Component
export type AllProps = IProps & IDispatch & IStateProps & IDispatchProps & IPropsFromParams;
export const ContactpointDetails = (props: AllProps) => {
    const classes = useStyles(useTheme());
    const { t: tContactpoint } = useTranslation('contactpoint');
    const { t: tCommon } = useTranslation('common');

    const loadData = () => props.onLoadData(props.match.params.id);

    React.useEffect(() => {
        const id = props.match.params.id;
        if (props.details.state === FetchState.NotFetched || (props.details.state === FetchState.Success && props.details.data.id !== id)) {
            props.onLoadData(id);
        }
    }, [props.match.params.id, props.details.state, props.details.data.id]);

    const loading = props.details.state !== FetchState.Success;
    const isDeleting = props.deleteState.state === FetchState.Busy;
    const hasDeleteError = props.deleteState.state === FetchState.Error;

    return <ContactpointNavigation navigations={props.navigations} >
        <PageHeader
                title={tContactpoint('title')}
                subTitle={tContactpoint('detailTitle', props.details.data)}
                explainText={tContactpoint('explainText')}
            />
        <ContactpointDetail
          data={props.details.data}
          loading={loading}
          actions={<>
                <RefreshButton onClick={loadData} disabled={props.details.state === FetchState.Busy} />
                <EditButton onClick={() => props.onEdit(props.details.data.id)} disabled={loading} />
                <DeleteButton onClick={() => props.onDelete(props.details.data.id)} disabled={loading} deleteWarningMessage={tContactpoint('deleteWarning', props.details.data)} />
            </>}
          />
        {props.details.state === FetchState.Error && <ErrorMessage errorDetails={props.details.errorMessage} message={tCommon('errorLoadingData')} title={tCommon('errorLoadingDataTitle')} options={[{optionLabel: tCommon('tryAgain'), optionExecute: loadData},{optionLabel: tCommon('restart'), optionExecute: () => window.location.reload()}]} />}
        
        {isDeleting && <DeletingMessage entity={tContactpoint('contactpoint')} />}
        {hasDeleteError && 
          <ErrorMessage 
            title={tCommon('deletingErrorTitle')} 
            message={tCommon('deletingErrorXMessage', {x: props.deleteState.errorMessage})} 
            canClose 
            options={[]} 
            errorDetails={props.deleteState.errorMessage} 
          />
        }
    </ContactpointNavigation>;
};

export default connect<IStateProps, IDispatchProps, {}, IApplicationState>(mapStateProps, mapDispatchProps)(ContactpointDetails);
